import { useEffect, useState } from "react";
import BookingForm from "@/components/BookingForm";

/**
 * Cal.com embed placeholder.
 *
 * To activate later: set REACT_APP_CAL_LINK in frontend/.env to your Cal.com
 * event path, for example "chief-ai-partners/free-15-min-consultation".
 *
 * If no link is configured, or if the embed script fails to load within a short
 * timeout, the fallback BookingForm is shown automatically.
 */
export default function CalEmbed() {
    const calLink = process.env.REACT_APP_CAL_LINK;
    const [embedReady, setEmbedReady] = useState(false);
    const [embedFailed, setEmbedFailed] = useState(false);

    useEffect(() => {
        if (!calLink) return;

        let cancelled = false;
        const timeoutId = setTimeout(() => {
            if (!cancelled && !embedReady) setEmbedFailed(true);
        }, 7000);

        const existing = document.getElementById("cal-embed-script");
        const loadAndMount = () => {
            const Cal = window.Cal;
            if (cancelled || typeof Cal !== "function") {
                setEmbedFailed(true);
                return;
            }
            try {
                Cal("init", { origin: "https://cal.com" });
                Cal("inline", {
                    elementOrSelector: "#cap-cal-embed",
                    calLink,
                    layout: "month_view",
                });
                Cal("ui", {
                    styles: { branding: { brandColor: "#101820" } },
                    hideEventTypeDetails: false,
                });
                setEmbedReady(true);
            } catch {
                setEmbedFailed(true);
            }
        };

        if (window.Cal) {
            loadAndMount();
        } else if (existing) {
            existing.addEventListener("load", loadAndMount);
            existing.addEventListener("error", () => setEmbedFailed(true));
        } else {
            const s = document.createElement("script");
            s.id = "cal-embed-script";
            s.src = "https://app.cal.com/embed/embed.js";
            s.async = true;
            s.onload = loadAndMount;
            s.onerror = () => setEmbedFailed(true);
            document.body.appendChild(s);
        }

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [calLink, embedReady]);

    if (!calLink || embedFailed) {
        return (
            <div data-testid="booking-fallback-container">
                <BookingForm />
            </div>
        );
    }

    return (
        <div data-testid="booking-cal-embed-container">
            <div
                id="cap-cal-embed"
                className="min-h-[640px] bg-white border border-sand"
                data-testid="cal-embed-slot"
            />
        </div>
    );
}
